import { Typography } from "@suraasa/placebo-ui"
import { UseQueryResult } from "@tanstack/react-query"
import { ITOCertificate } from "api/resources/profile/types"
import NoCertificates from "assets/Fallback/no-certificates.svg"
import { ReactComponent as Ito } from "assets/MyCertificates/ItoColor.svg"
import clsx from "clsx"
import Fallback from "components/Fallback"
import FullPageLoading from "components/FullPageLoading"
import ShareSheet from "components/ShareSheet"
import CpatLogo from "features/Cpat/assets/cpat-mini-logo.webp"
import React, { useState } from "react"

import CpatCard from "../Cards/CpatCard"
import ItoCard from "../Cards/ItoCard"
import styles from "./OtherCertificates.module.css"

type Props = { othersList: UseQueryResult<ITOCertificate[]> }

const OtherCertificates = (props: Props) => {
  const [certificateUrl, setCertificateUrl] = useState<string>("")

  if (props.othersList.isError) {
    return (
      <Fallback
        data={{
          image: NoCertificates,
          title: "Something went wrong!",
          description: "Please try again later.",
        }}
        className="fixed left-1/2 top-1/2 w-full -translate-x-1/2 -translate-y-1/2 px-2 text-center sm:w-fit sm:px-0"
        hasError={true}
      />
    )
  }

  if (!props.othersList.data || props.othersList.isLoading) {
    return <FullPageLoading />
  }

  if (props.othersList.data.length === 0) {
    return (
      <Fallback
        data={{
          image: NoCertificates,
          title: "Looks like you don’t have any certificates yet!",
          description:
            "Participate in events conducted by Suraasa to earn certificates",
        }}
        className="fixed left-1/2 top-1/2 w-full -translate-x-1/2 -translate-y-1/2 px-2 text-center sm:w-fit sm:px-0"
        hasError={false}
      />
    )
  }

  const data2022 = props.othersList.data.filter(
    x => x.item.itemType === "olympiad_award" && x.item.olympiad.year === 2022
  )
  const data2023 = props.othersList.data.filter(
    x => x.item.itemType === "olympiad_award" && x.item.olympiad.year === 2023
  )

  const cpatCertificates = props.othersList.data.filter(
    x => x.item.itemType === "cpat_assessment"
  )

  return (
    <>
      {cpatCertificates.length > 0 && (
        <div className="mb-3">
          <div className="mb-2 flex items-center">
            <img src={CpatLogo} alt="" className="mr-1.5 size-4" />
            <Typography variant="title2">
              Comprehensive Pedagogy Assessment of Teachers
            </Typography>
          </div>
          <div
            className={clsx(
              "mb-4 grid gap-1 sm:gap-x-3 sm:gap-y-2 md:gap-x-2",
              styles.gridContainer
            )}
          >
            {cpatCertificates.map((item, index) => (
              <CpatCard key={index} file={item.file} issuedAt={item.issuedAt} />
            ))}
          </div>
        </div>
      )}

      {data2023.length > 0 && (
        <div className="mb-3">
          <div className="mb-2 flex items-center">
            <Ito className="me-2" />
            <Typography variant="title2">
              International Teachers’ Olympiad 2023
            </Typography>
          </div>
          <div
            className={clsx(
              "mb-4 grid gap-1 sm:gap-x-3 sm:gap-y-2 md:gap-x-2",
              styles.gridContainer
            )}
          >
            {data2023.map((item, index) => (
              <ItoCard
                setCertificateUrl={url => setCertificateUrl(url)}
                key={index}
                data={item}
              />
            ))}
          </div>
        </div>
      )}

      {data2022.length > 0 && (
        <div className="mb-3">
          <div className="mb-2 flex items-center">
            <Ito className="me-2" />
            <Typography variant="title2">
              International Teachers’ Olympiad 2022
            </Typography>
          </div>
          <div
            className={clsx(
              "mb-4 grid gap-1 sm:gap-x-3 sm:gap-y-2 md:gap-x-2",
              styles.gridContainer
            )}
          >
            {data2022.map((item, index) => (
              <ItoCard
                setCertificateUrl={url => setCertificateUrl(url)}
                key={index}
                data={item}
              />
            ))}
          </div>
        </div>
      )}

      <ShareSheet
        data={{
          url: certificateUrl,
          text: `Checkout my certificate on Suraasa: ${certificateUrl}`,
        }}
        open={!!certificateUrl}
        handleClose={() => {
          setCertificateUrl("")
        }}
      />
    </>
  )
}

export default OtherCertificates
